'use client';

import {useState, useEffect} from 'react';
import {useLocale, useTranslations} from 'next-intl';

interface Projet {
  id: number;
  titre_fr: string;
  categorie: string;
  statut: string;
  budget_objectif: number;
  montant_collecte: number;
  gouvernorat?: string;
  delegation?: string;
}

const statutStyles: Record<string, {bg: string; text: string}> = {
  publie: {bg: 'bg-blue-100', text: 'text-blue-700'},
  en_cours: {bg: 'bg-green-100', text: 'text-green-700'},
  en_collecte: {bg: 'bg-orange-100', text: 'text-orange-700'},
  finance: {bg: 'bg-green-100', text: 'text-green-700'},
  termine: {bg: 'bg-gray-100', text: 'text-gray-600'},
  urgence: {bg: 'bg-red-100', text: 'text-red-700'},
  brouillon: {bg: 'bg-gray-100', text: 'text-gray-500'},
  soumis: {bg: 'bg-yellow-100', text: 'text-yellow-700'},
  instruction: {bg: 'bg-purple-100', text: 'text-purple-700'},
  valide: {bg: 'bg-blue-100', text: 'text-blue-700'},
  rejete: {bg: 'bg-red-100', text: 'text-red-600'},
};

export default function ProjetsPage() {
  const locale = useLocale();
  const t = useTranslations('projects_page');
  const tw = useTranslations('workflow');
  const [projets, setProjets] = useState<Projet[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtreCat, setFiltreCat] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');
  const [filtreGouv, setFiltreGouv] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 24;

  useEffect(() => {
    // Check if user is staff to load all statuses
    const stored = localStorage.getItem('utss_user');
    const user = stored ? JSON.parse(stored) : null;
    const isStaff = user?.role && user.role !== 'donateur';
    const statutParam = isStaff ? '&statut=all' : '';
    fetch(`/api/v1/projets/?limit=100${statutParam}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { setProjets(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = projets.filter(p => {
    if (filtreCat && p.categorie !== filtreCat) return false;
    if (filtreStatut && p.statut !== filtreStatut) return false;
    if (filtreGouv && p.gouvernorat !== filtreGouv) return false;
    if (search && !p.titre_fr.toLowerCase().includes(search.toLowerCase()) && !(p.gouvernorat || '').toLowerCase().includes(search.toLowerCase()) && !(p.delegation || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const categories = [...new Set(projets.map(p => p.categorie).filter(Boolean))].sort();
  const statuts = [...new Set(projets.map(p => p.statut).filter(Boolean))].sort();
  const gouvernorats = [...new Set(projets.map(p => p.gouvernorat).filter(Boolean))].sort();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-primary">{t('title')}</h1>
          <p className="text-muted text-sm mt-0.5">{t('subtitle', {count: filtered.length})}{filtreGouv ? ` a ${filtreGouv}` : ''}</p>
        </div>
        <a href={`/${locale}/structures`} style={{color: '#1B5E20', textDecorationColor: 'transparent'}} className="inline-flex items-center gap-1.5 border border-primary text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-primary/5 transition-colors no-underline">
          {t('view_map')}
        </a>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <input
          type="text"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          placeholder={t('search_placeholder')}
          className="px-3 py-1.5 text-sm border border-border rounded-lg bg-white w-48 focus:ring-1 focus:ring-primary focus:border-primary outline-none"
        />
        <select value={filtreCat} onChange={e => { setFiltreCat(e.target.value); setPage(0); }} className="px-2 py-1.5 text-xs border border-border rounded-lg bg-white">
          <option value="">{t('all_categories')}</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filtreStatut} onChange={e => { setFiltreStatut(e.target.value); setPage(0); }} className="px-2 py-1.5 text-xs border border-border rounded-lg bg-white">
          <option value="">{t('all_statuses')}</option>
          {statuts.map(s => <option key={s} value={s}>{tw(`status_${s}`)}</option>)}
        </select>
        <select value={filtreGouv} onChange={e => { setFiltreGouv(e.target.value); setPage(0); }} className="px-2 py-1.5 text-xs border border-border rounded-lg bg-white">
          <option value="">{t('all_governorates')}</option>
          {gouvernorats.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        {(search || filtreCat || filtreStatut || filtreGouv) && (
          <button onClick={() => { setSearch(''); setFiltreCat(''); setFiltreStatut(''); setFiltreGouv(''); setPage(0); }} className="text-xs text-muted hover:text-secondary cursor-pointer">
            {t('reset')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted">{t('loading')}</div>
      ) : paged.length === 0 ? (
        <div className="text-center py-12 text-muted">{t('no_results')}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paged.map(p => {
              const pct = p.budget_objectif > 0 ? Math.round((p.montant_collecte / p.budget_objectif) * 100) : 0;
              const s = statutStyles[p.statut] || {bg: 'bg-gray-100', text: 'text-gray-600'};
              return (
                <a key={p.id} href={`/${locale}/projets/${p.id}`} style={{textDecorationColor: 'transparent'}} className="block rounded-xl border border-border bg-white hover:shadow-md transition-shadow p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-sm font-semibold text-foreground leading-tight flex-1 mr-2">{p.titre_fr}</h3>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0 ${s.bg} ${s.text}`}>{tw(`status_${p.statut}`)}</span>
                  </div>
                  <div className="text-xs text-muted mb-3 capitalize">{p.categorie}{p.gouvernorat ? ` — ${p.gouvernorat}` : ''}{p.delegation ? ` > ${p.delegation}` : ''}</div>
                  <div className="h-1.5 bg-border rounded-full overflow-hidden mb-1">
                    <div className={`h-full rounded-full ${pct >= 100 ? 'bg-green-500' : pct >= 50 ? 'bg-primary' : 'bg-orange-500'}`} style={{width: `${Math.min(pct, 100)}%`}} />
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-muted">{p.montant_collecte?.toLocaleString()} / {p.budget_objectif?.toLocaleString()} TND</span>
                    <span className="font-bold" style={{color: pct >= 100 ? '#2E7D32' : '#1B5E20'}}>{pct}%</span>
                  </div>
                </a>
              );
            })}
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1 text-xs border border-border rounded hover:bg-surface disabled:opacity-30 cursor-pointer">{t('previous')}</button>
              <span className="text-xs text-muted">Page {page + 1} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="px-3 py-1 text-xs border border-border rounded hover:bg-surface disabled:opacity-30 cursor-pointer">{t('next')}</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
